import { useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Check } from "lucide-react";

import UserInfoModal from "containers/ModalContainer/modals/UserInfoModal";

dayjs.extend(relativeTime);

const RECENT_THRESHOLD_DAYS = 90;

const EMPTY_RECENCY = { date: null, isRecent: false, source: null };

const getActivityRecency = (hit = {}) => {
  if (!hit.lastActivityDate && !hit.createdAt) return EMPTY_RECENCY;

  const recencySource = hit.lastActivityDate ? "activity" : "created";
  const selectedDate = hit.lastActivityDate || hit.createdAt;
  const recencyDate = dayjs(selectedDate);

  if (!recencyDate.isValid()) return EMPTY_RECENCY;

  return {
    date: recencyDate,
    isRecent: dayjs().diff(recencyDate, "day") < RECENT_THRESHOLD_DAYS,
    source: recencySource,
  };
};

const ActivityHistoryDialogTrigger = ({
  children,
  className,
  hit,
  match,
  onClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = (event) => {
    onClick?.(event);
    setIsOpen(true);
  };

  return (
    <>
      <button type="button" onClick={handleClick} className={className}>
        {children}
      </button>

      <UserInfoModal
        open={isOpen}
        onOpenChange={setIsOpen}
        match={match}
        user={hit}
        tabDefaultValue="activity-history"
      />
    </>
  );
};

export const UpdateRecencyBadge = ({
  hit,
  vertical,
  variant = "default",
  isClickable = false,
}) => {
  const { date, isRecent, source } = getActivityRecency(hit);

  if (!date) return null;

  const actionLabel = source === "created" ? "Created" : "Last activity";
  const label = source === "created" ? "Recently Created" : "Recent Activity";
  const relativeTime = date.fromNow();

  const stopTriggerPropagation = (e) => e.stopPropagation();

  if (variant === "listView") {
    const match = hit?.match || {};
    const prefix = source === "created" ? "Created" : "Activity";
    const text = `${prefix} ${relativeTime}`;
    const content = isRecent ? (
      <>
        <div className="bg-sky-500 rounded-full inline-flex p-0.5 shrink-0">
          <Check className="text-white" strokeWidth={3} size={11} />
        </div>
        <span className="text-sm font-medium truncate">{text}</span>
      </>
    ) : (
      <span>{text}</span>
    );

    if (!isClickable) {
      const StaticElement = isRecent ? "div" : "span";
      const staticClassName = isRecent
        ? "flex items-center gap-1.5 min-w-0"
        : "text-sm font-normal truncate inline-block";

      return (
        <StaticElement className={staticClassName}>{content}</StaticElement>
      );
    }

    const className = `text-sky-500 ${
      isRecent
        ? "flex items-center gap-1.5 min-w-0 border-0 bg-transparent text-left"
        : "border-0 bg-transparent text-left text-sm font-normal truncate inline-block"
    } -mx-2 -my-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors cursor-pointer`;

    return (
      <ActivityHistoryDialogTrigger
        className={className}
        hit={hit}
        match={match}
        onClick={stopTriggerPropagation}
      >
        {content}
      </ActivityHistoryDialogTrigger>
    );
  }

  const content = (
    <>
      {isRecent && (
        <div className="flex flex-row gap-1 items-center">
          <div className="bg-sky-500 rounded-full inline-flex p-0.5">
            <Check className="text-white" strokeWidth={3} size={11} />
          </div>

          <span className="text-sm font-semibold">{label}</span>
        </div>
      )}

      <span className="text-xs font-normal">
        {isRecent ? relativeTime : `${actionLabel} ${relativeTime}`}
      </span>
    </>
  );

  if (!isClickable) {
    const StaticElement = isRecent ? "div" : "span";
    const staticClassName = isRecent
      ? "flex items-center gap-1.5 min-w-0"
      : "text-sm font-normal truncate inline-block";

    return <StaticElement className={staticClassName}>{content}</StaticElement>;
  }

  const match =
    hit?.match?.applicationId || hit?.associatedMatch?.applicationId || {};

  return (
    <ActivityHistoryDialogTrigger
      className={`flex cursor-pointer border-0 bg-transparent p-0 items-baseline  text-sky-500 hover:underline w-fit ${
        vertical ? "flex-col gap-0" : "flex-row gap-1 items-center"
      }`}
      hit={hit}
      match={match}
      onClick={stopTriggerPropagation}
    >
      {content}
    </ActivityHistoryDialogTrigger>
  );
};
